import React from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';

/* PIPE-INST T5 (firma #126) — EL MODAL DE «ELIMINAR GRUPO».
 *
 * ── EL COPY ES VERBATIM DE LA FIRMA #113-v2, NI UNA LETRA DISTINTA ─────────
 *
 * Desde la TANDA 26 (firma #234) vive en `dashboard:groups.delete_group_*` —bajo
 * `groups.*` y no bajo `groups.bank.*`, porque lo que se destruye es el GRUPO
 * entero— y `profeBilingue.tanda26.test.js` lo fija carácter por carácter en LOS
 * DOS idiomas. Es el único aviso del producto de profesor que anuncia una
 * destrucción de material pagado, así que el texto no admite variaciones «que
 * suenan igual»: enumera lo que muere, dice «para siempre», dice que los alumnos
 * dejarán de ver el grupo, y dice que no se puede deshacer. El candado comprueba
 * que el inglés conserve las dos frases que hacen el trabajo («permanently»,
 * «cannot be undone») y que NINGUNO de los dos mencione flashcards, que es la
 * enmienda de T5.1 y la tentación más obvia al traducir una lista.
 *
 * ── POR QUÉ UN COMPONENTE Y NO UN BLOQUE DENTRO DE LA PANTALLA ─────────────
 *
 * Por el mismo criterio con el que existe `RegenerateCodeModal`: un modal
 * destructivo se prueba solo, sin montar la página del grupo entera con su
 * lista de alumnos y su banco. Y por el nombre — lleva «Group» a propósito, para
 * caer dentro del censo de superficies de grupos (`piloto1.superficies.test.js`)
 * y de su barrido de imports prohibidos. Es la lección que T4.5 dejó escrita al
 * bautizar `GroupBankVigilante`: un archivo institucional con un nombre que el
 * censo no ve pasa sin auditar.
 *
 * ── LA JERARQUÍA DE LOS BOTONES ────────────────────────────────────────────
 *
 * «Cancelar» es el PRIMARIO (naranja, el sitio donde cae el dedo) y «Eliminar
 * grupo y todo su contenido» el secundario en rojo. Es la misma repartición que
 * el modal de borrar un documento del banco ya usa, y por la misma razón: en un
 * diálogo destructivo, el botón cómodo tiene que ser el que no destruye.
 */

export default function GroupDeleteModal({ abierto, borrando, onCancel, onConfirm }) {
  const { t } = useTranslation(['dashboard', 'common']);
  if (!abierto) return null;
  return (
    /* `fixed` → escribe su propia safe area abajo, igual que los otros modales de
       esta familia: no hereda ninguna del chasis. */
    <div className="fixed top-14 inset-x-0 bottom-0 z-[60] bg-black/80 flex
      items-center justify-center p-4" data-testid="group-delete-modal">
      <div className="relative bg-[#111] border border-[#262626] rounded-[20px]
        p-6 sm:p-8 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] max-w-md w-full
        mx-4 shadow-2xl">
        <h3 className="text-lg font-semibold text-white mb-2"
          data-testid="group-delete-title">
          {t('groups.delete_group_title')}
        </h3>
        <p className="text-sm text-[#a0a0a0] mb-5" data-testid="group-delete-body">
          {t('groups.delete_group_body')}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={onCancel}
            className="flex-1 bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl
              min-h-[44px] text-sm"
            data-testid="group-delete-cancel">
            {t('common:cancel')}
          </Button>
          <Button variant="outline" onClick={onConfirm} disabled={borrando}
            className="flex-1 border-[#333] text-red-400 hover:text-red-300
              rounded-xl min-h-[44px] text-sm disabled:opacity-40"
            data-testid="group-delete-confirm">
            {borrando
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : t('groups.delete_group_confirm_btn')}
          </Button>
        </div>
      </div>
    </div>
  );
}
